import { useSyncExternalStore } from "react";

/**
 * The colour theme. "system" follows the device; "light" and "dark" are the
 * participant's choice, kept in localStorage under `theme`. The inline script
 * in public/index.html applies the same rule before the app loads, so a dark
 * device never flashes white.
 */
export const THEMES = ["system", "light", "dark"];

const STORAGE_KEY = "theme";
const listeners = new Set();

const darkQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

function readStoredTheme() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

let currentTheme = readStoredTheme();

function resolveTheme(theme) {
  if (theme === "system") {
    return darkQuery().matches ? "dark" : "light";
  }
  return theme;
}

function applyTheme() {
  const resolved = resolveTheme(currentTheme);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

if (typeof document !== "undefined") {
  applyTheme();
}

function notify() {
  listeners.forEach(listener => listener());
}

function setTheme(theme) {
  currentTheme = THEMES.includes(theme) ? theme : "system";
  try {
    window.localStorage.setItem(STORAGE_KEY, currentTheme);
  } catch {
    // Storage blocked: the choice lasts until the page is closed.
  }
  applyTheme();
  notify();
}

function subscribe(listener) {
  listeners.add(listener);
  const query = darkQuery();
  const onDeviceChange = () => {
    if (currentTheme === "system") {
      applyTheme();
      notify();
    }
  };
  // Safari before 14 only has the deprecated addListener.
  if (query.addEventListener) {
    query.addEventListener("change", onDeviceChange);
  } else {
    query.addListener(onDeviceChange);
  }
  return () => {
    listeners.delete(listener);
    if (query.removeEventListener) {
      query.removeEventListener("change", onDeviceChange);
    } else {
      query.removeListener(onDeviceChange);
    }
  };
}

/** `theme` is the choice, `resolvedTheme` what is on screen. */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => currentTheme);
  const resolvedTheme = useSyncExternalStore(subscribe, () =>
    resolveTheme(currentTheme)
  );
  return { theme, resolvedTheme, setTheme };
}
